import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  Typography,
  Grid,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { generateClient } from "aws-amplify/api";
import { getCurrentUser } from "aws-amplify/auth";
import { startBRJob } from "../graphql/mutations";
import * as APITypes from "../models/API";

const client = generateClient();

interface ReportComponentProps {
  isOpen: boolean;
  menuWidth: number;
}

const ReportComponent: React.FC<ReportComponentProps> = ({
  isOpen,
  menuWidth,
}) => {
  const theme = useTheme();
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [region, setRegion] = useState("");
  const [metrics, setMetrics] = useState<Record<string, boolean>>({
    RPOM: false,
    "Page CTR": false,
    CPC: false,
    Coverage: false,
    "Page Ad Count": false,
  });
  const [surfaces, setSurfaces] = useState<Record<string, boolean>>({
    Desktop: false,
    Mobile: false,
  });
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);
  const [jobResult, setJobResult] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string>("");

  const regions = ["NA", "EU", "FE", "SA", "CN"] as const;

  // Get authenticated user email
  useEffect(() => {
    const getUserEmail = async () => {
      try {
        const currentUser = await getCurrentUser();
        const email =
          currentUser.signInDetails?.loginId || currentUser.username;
        setUserEmail(email);
      } catch (error) {
        console.error("Failed to get authenticated user:", error);
      }
    };
    getUserEmail();
  }, []);

  const handleMetricChange = (metric: string) => {
    setMetrics((prev) => ({ ...prev, [metric]: !prev[metric] }));
  };

  const handleSurfaceChange = (surface: string) => {
    setSurfaces((prev) => ({ ...prev, [surface]: !prev[surface] }));
  };

  const handleGenerateReport = async () => {
    console.log("Starting report generation...");

    if (!userEmail) {
      setJobResult(
        "Error: User not authenticated. Please refresh and try again."
      );
      return;
    }

    const hasMetrics = Object.values(metrics).some(Boolean);
    const hasSurfaces = Object.values(surfaces).some(Boolean);

    const newErrors: Record<string, boolean> = {
      startDate: !startDate,
      endDate: Boolean(
        !endDate ||
          (startDate && endDate && new Date(endDate) < new Date(startDate))
      ),
      region: !region,
      metrics: !hasMetrics,
      surfaces: !hasSurfaces,
    };

    setErrors(newErrors);

    if (Object.values(newErrors).some(Boolean)) {
      console.log("Validation errors:", newErrors);
      return;
    }

    setLoading(true);
    setJobResult(null);

    try {
      const selectedMetrics = Object.keys(metrics).filter(
        (key) => metrics[key]
      );
      const selectedDevices = Object.keys(surfaces).filter(
        (key) => surfaces[key]
      );

      // Format email for GraphQL - convert AmazonFederate_username to username@amazon.com
      let displayEmail = userEmail;
      if (userEmail && !userEmail.includes("@")) {
        if (userEmail.startsWith("AmazonFederate_")) {
          const username = userEmail.replace("AmazonFederate_", "");
          displayEmail = `${username}@amazon.com`;
        } else {
          displayEmail = `${userEmail}@amazon.com`;
        }
      }

      const input: APITypes.StartBRJobInput = {
        user_id: 0,
        user_email: displayEmail,
        report_type: "weekly",
        date_range: JSON.stringify({
          start_date: startDate,
          end_date: endDate,
        }),
        metrics: selectedMetrics,
        regions: [region],
        devices: selectedDevices,
        environment: "alpha",
      } as any;

      console.log("GraphQL input:", input);

      const result = (await client.graphql({
        query: startBRJob,
        variables: { input },
      })) as { data: APITypes.StartBRJobMutation };

      console.log("Full GraphQL result:", JSON.stringify(result, null, 2));
      console.log("startBRJob data:", result.data?.startBRJob);
      console.log("GraphQL errors:", (result as any).errors);

      if ((result as any).errors && (result as any).errors.length > 0) {
        const errorMessages = (result as any).errors
          .map((err: any) => err.message)
          .join(", ");
        setJobResult(`GraphQL Error: ${errorMessages}`);
        return;
      }

      if (result.data?.startBRJob?.success) {
        // Format email for display - convert AmazonFederate_username to username@amazon.com
        let displayEmail = userEmail;
        if (userEmail && !userEmail.includes("@")) {
          if (userEmail.startsWith("AmazonFederate_")) {
            const username = userEmail.replace("AmazonFederate_", "");
            displayEmail = `${username}@amazon.com`;
          } else {
            displayEmail = `${userEmail}@amazon.com`;
          }
        }

        setJobResult(
          `Report job started successfully! Job ID: ${result.data.startBRJob.job_id}. You will receive an email at ${displayEmail} when the report is ready.`
        );
      } else if (result.data?.startBRJob === null) {
        console.error(
          "GraphQL mutation returned null - likely Lambda function error"
        );
        setJobResult(
          "Error: The BR job service returned null. Check Lambda function logs and AppSync resolver configuration."
        );
      } else {
        const errorMsg =
          result.data?.startBRJob?.error ||
          result.data?.startBRJob?.message ||
          "Unknown error";
        console.error("BR Job failed:", result.data?.startBRJob);
        setJobResult(`Error: ${errorMsg}`);
      }
    } catch (error) {
      console.error("GraphQL Error:", error);
      console.error("Error details:", {
        name: error instanceof Error ? error.name : "Unknown",
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      });

      let errorMessage = "Unknown error occurred";
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === "string") {
        errorMessage = error;
      } else if (error && typeof error === "object") {
        errorMessage = JSON.stringify(error);
      }

      setJobResult(`Failed to start report: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        p: 3,
        ml: `${menuWidth}px`,
        transition: theme.transitions.create("margin-left", {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.standard,
        }),
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        pt: 6,
      }}
    >
      <Box sx={{ maxWidth: 600, width: "100%" }}>
        <Typography variant="h4" gutterBottom align="center">
          Generate Report
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Date Range
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                error={errors.startDate}
                helperText={errors.startDate ? "Start Date is required" : ""}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="End Date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                error={errors.endDate}
                helperText={
                  errors.endDate
                    ? !endDate
                      ? "End Date is required"
                      : "End Date must be after Start Date"
                    : ""
                }
                fullWidth
              />
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Region
          </Typography>
          <FormControl fullWidth error={errors.region}>
            <InputLabel>Region</InputLabel>
            <Select
              value={region}
              label="Region"
              onChange={(e) => setRegion(e.target.value)}
            >
              {regions.map((r) => (
                <MenuItem key={r} value={r}>
                  {r}
                </MenuItem>
              ))}
            </Select>
            {errors.region && (
              <Typography
                variant="caption"
                color="error"
                sx={{ mt: 0.5, ml: 1.75 }}
              >
                Region is required
              </Typography>
            )}
          </FormControl>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            gutterBottom
            color={errors.metrics ? "error" : "inherit"}
          >
            Metrics
          </Typography>
          <FormGroup>
            {Object.keys(metrics).map((metric) => (
              <FormControlLabel
                key={metric}
                control={
                  <Checkbox
                    checked={metrics[metric]}
                    onChange={() => handleMetricChange(metric)}
                  />
                }
                label={metric}
              />
            ))}
          </FormGroup>
          {errors.metrics && (
            <Typography variant="caption" color="error" sx={{ ml: 1.75 }}>
              At least one metric is required
            </Typography>
          )}
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            gutterBottom
            color={errors.surfaces ? "error" : "inherit"}
          >
            Surfaces
          </Typography>
          <FormGroup>
            {Object.keys(surfaces).map((surface) => (
              <FormControlLabel
                key={surface}
                control={
                  <Checkbox
                    checked={surfaces[surface]}
                    onChange={() => handleSurfaceChange(surface)}
                  />
                }
                label={surface}
              />
            ))}
          </FormGroup>
          {errors.surfaces && (
            <Typography variant="caption" color="error" sx={{ ml: 1.75 }}>
              At least one surface is required
            </Typography>
          )}
        </Box>

        {jobResult && (
          <Alert
            severity={jobResult.includes("successfully") ? "success" : "error"}
            sx={{ mb: 2 }}
          >
            {jobResult}
          </Alert>
        )}

        <Box sx={{ textAlign: "center", mt: 4 }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleGenerateReport}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? "Starting Report..." : "Generate and Send Report"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ReportComponent;
